/**
 * Utility functions for extracting and normalizing county information
 * from address strings.
 */

/**
 * Extract county name from an address string.
 * Handles various address formats from Google Places API.
 *
 * @param {string} address - Full address string (e.g., "123 Main St, Nevada City, CA 95959, USA")
 * @returns {string|null} - County name normalized for matching, or null if not found
 */
export const extractCountyFromAddress = (address) => {
  if (!address || typeof address !== 'string') {
    return null;
  }

  // Common California county patterns to look for in addresses
  // The Google Places API often includes county names in various formats
  const countyPatterns = [
    // Pattern: "County Name County, CA" or "County Name, CA"
    /([A-Za-z\s]+)\s+County,?\s*CA/i,
    // Pattern: Extract from full address - look for city names that map to counties
  ];

  for (const pattern of countyPatterns) {
    const match = address.match(pattern);
    if (match && match[1]) {
      return normalizeCountyName(match[1].trim());
    }
  }

  // If no county pattern found, try to infer from known city-to-county mappings
  const cityCountyMap = getCityToCountyMap();
  const addressLower = address.toLowerCase();

  for (const [city, county] of Object.entries(cityCountyMap)) {
    if (addressLower.includes(city.toLowerCase())) {
      return county;
    }
  }

  return null;
};

/**
 * Normalize county name for consistent matching.
 * Removes common suffixes and normalizes casing.
 *
 * @param {string} county - Raw county name
 * @returns {string} - Normalized county name (lowercase, snake_case)
 */
export const normalizeCountyName = (county) => {
  if (!county) return null;

  return county
    .toLowerCase()
    .replace(/\s+county$/i, '') // Remove " County" suffix
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .trim();
};

/**
 * Map of cities to their counties for California fire-prone areas.
 * This helps when the address doesn't explicitly include the county name.
 */
const getCityToCountyMap = () => ({
  // Nevada County
  'nevada city': 'nevada',
  'grass valley': 'nevada',
  'penn valley': 'nevada',
  'lake of the pines': 'nevada',
  'alta sierra': 'nevada',
  'north san juan': 'nevada',
  'rough and ready': 'nevada',
  'chicago park': 'nevada',
  'washington': 'nevada',

  // Placer County
  'auburn': 'placer',
  'roseville': 'placer',
  'lincoln': 'placer',
  'rocklin': 'placer',
  'loomis': 'placer',
  'colfax': 'placer',
  'foresthill': 'placer',
  'tahoe city': 'placer',
  'kings beach': 'placer',
  'truckee': 'nevada', // Actually in Nevada County

  // El Dorado County
  'placerville': 'el_dorado',
  'el dorado hills': 'el_dorado',
  'cameron park': 'el_dorado',
  'shingle springs': 'el_dorado',
  'pollock pines': 'el_dorado',
  'south lake tahoe': 'el_dorado',

  // Yuba County
  'marysville': 'yuba',
  'wheatland': 'yuba',
  'olivehurst': 'yuba',
  'browns valley': 'yuba',

  // Sierra County
  'downieville': 'sierra',
  'sierra city': 'sierra',
  'loyalton': 'sierra',

  // Butte County
  'paradise': 'butte',
  'chico': 'butte',
  'oroville': 'butte',
  'magalia': 'butte',

  // Sacramento County (edge of WUI)
  'sacramento': 'sacramento',
  'folsom': 'sacramento',
  'rancho cordova': 'sacramento',
  'elk grove': 'sacramento',
});

/**
 * Get a display-friendly county name from the normalized value.
 *
 * @param {string} normalizedCounty - Normalized county name (e.g., "el_dorado")
 * @returns {string} - Display name (e.g., "El Dorado County")
 */
export const getCountyDisplayName = (normalizedCounty) => {
  const displayNames = {
    nevada: 'Nevada County',
    placer: 'Placer County',
    el_dorado: 'El Dorado County',
    yuba: 'Yuba County',
    sierra: 'Sierra County',
    butte: 'Butte County',
    sacramento: 'Sacramento County',
  };

  return displayNames[normalizedCounty] || normalizedCounty;
};
